import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import CensusCityStats from '../../components/census/CensusCityStats';
import CensusGauges from '../../components/census/CensusGauges';
import CensusLevelPyramid from '../../components/census/CensusLevelPyramid';
import CensusTreemap from '../../components/census/CensusTreemap';
import Spinner from '../../components/Spinner';
import formatNumber from '../../utils/formatNumber';

const COLORS = ['#84C341', '#00C49F', '#0088FE', '#FFBB28', '#FF8042', '#AF19FF'];

const CensusData = () => {
  const { data, isLoading } = useQuery({
    queryKey: ['census', 'statistics'],
    queryFn: async () => {
      const response = await fetch('/api/census/statistics');
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      return response.json();
    },
    staleTime: 1000 * 60 * 60 * 24,
  });

  if (isLoading) {
    return (
      <div className='mt-4'>
        <Spinner />
      </div>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <div className='mb-24 lg:mb-48'>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className='relative'>
        <div className='relative mx-auto max-w-7xl'>
          <div className='max-w-4xl mx-auto'>
            <dl
              className='rounded-2xl shadow-lg sm:grid sm:grid-cols-3 overflow-hidden'
              style={{
                background: 'linear-gradient(135deg, rgba(38,38,38,0.8) 0%, rgba(23,23,23,0.9) 100%)',
                border: '1px solid rgba(255,255,255,0.06)',
              }}
            >
              {[
                { label: 'Kitty Shitizens', value: data.kittiesLivingInCities, color: '#84C341' },
                { label: 'Other Shitizens', value: data.otherCitizens, color: '#00C49F' },
                { label: 'Total Shitizens', value: data.totalCitizens, color: '#FFBB28' },
              ].map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                  className='flex flex-col p-4 lg:p-6 text-center border-b border-neutral-800 sm:border-0 sm:border-r last:border-r-0'
                >
                  <dt className='order-2 mt-2 text-sm lg:text-lg font-medium leading-6 text-neutral-500'>{stat.label}</dt>
                  <dd className='order-1 text-2xl lg:text-4xl font-extrabold' style={{ color: stat.color }}>
                    {formatNumber(stat.value)}
                  </dd>
                </motion.div>
              ))}
            </dl>
          </div>
        </div>
      </motion.div>

      {data.cityStats && <CensusGauges cityStats={data.cityStats} />}

      {data.populationByGeneration && <CensusTreemap populationByGeneration={data.populationByGeneration} />}

      {data.citizensByLevel && <CensusLevelPyramid citizensByLevel={data.citizensByLevel} />}

      {data.cityStats && <CensusCityStats cityStats={data.cityStats} totalCities={data.totalCities} />}
    </div>
  );
};

export default CensusData;
